<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {AvailabilityState, CabMe} from 'common';
import {logout} from '@/base/auth.js';
import {clearMe, getMeInfo} from '@/base/me.js';
import {stopImpersoneren} from '@/base/callBackend';
import {stateForReporters} from '@/base/TMP_globalState.js';
import {ElDropdown, ElDropdownItem, ElDropdownMenu} from 'element-plus';

const router = useRouter();

const meInfo         = ref<CabMe | undefined>(undefined);
const userName       = computed(() => getUserName());
const userInitials   = computed(() => getUserInitials());
const stateSuffix    = computed(() => getStateSuffix());
const userPermission = computed(() => {
    const perms = meInfo.value?.permissions?.join() ?? '';
    return stateSuffix.value ? `${perms} (${stateSuffix.value})` : perms;
});

onMounted(async () => {
    meInfo.value = await getMeInfo();
});

async function onLogout() {
    try {
        await logout();
    } finally {
        clearMe();
        router.push('/');
    }
}

async function onStopImpersoneren() {
    await stopImpersoneren();
    clearMe();
    window.location.assign('/');
}

function getUserName(): string {
    return meInfo.value?.name || meInfo.value?.email || '?';
}

function getUserInitials(): string {
    const woorden: string[] = getUserName().split(/\s+/).filter((w: string) => w !== '');
    return woorden.map((w: string) => w[0]).join('').toUpperCase() || '?';
}

function getStateSuffix() {
    if (meInfo.value?.permissions?.includes('report') ?? false) {
        switch (stateForReporters.value) {
            case AvailabilityState.closed:
                return 'geen-toegang';
            case AvailabilityState.read:
                return 'alleen-lezen';
        }
    }
    return '';
}
</script>

<template>
    <el-dropdown trigger="click">
        <button data-testid="current-user-button" :title="userName" class="flex items-center gap-3 px-1.5 py-2 rounded-control hover:bg-white/15 cursor-pointer w-full text-left">
            <span class="w-8 h-8 rounded-full bg-white text-zzc-blauw text-sm flex items-center justify-center font-semibold shrink-0">
                {{ userInitials }}
            </span>
            <span class="flex flex-col items-start leading-tight whitespace-nowrap opacity-0 lg:group-hover:opacity-100 transition-opacity">
                <span class="text-sm text-white truncate max-w-[140px]">{{ userName }}</span>
                <span v-if="stateSuffix" class="text-xs text-zzc-warning">{{ stateSuffix }}</span>
            </span>
        </button>
        <template #dropdown>
            <div class="px-4 py-2 border-b border-gray-100 min-w-48">
                <div class="text-sm text-gray-800">{{ userName }}</div>
                <div data-testid="current-user-permission" class="text-xs text-gray-500">{{ userPermission }}</div>
            </div>
            <el-dropdown-menu>
                <el-dropdown-item v-if="meInfo?.echtEmail" @click="onStopImpersoneren">Stop impersoneren</el-dropdown-item>
                <el-dropdown-item @click="onLogout">Uitloggen</el-dropdown-item>
            </el-dropdown-menu>
        </template>
    </el-dropdown>
</template>
